import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
} from '@mui/material';
import {
  TableChart as TableChartIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import { LoadingOrb } from '../components/LoadingOrb';

interface ConversionRow {
  id: number;
  percentileMin: number;
  percentileMax: number;
  zScoreMin: number | null;
  zScoreMax: number | null;
  tScoreMin: number | null;
  tScoreMax: number | null;
  standardScoreMin: number | null;
  standardScoreMax: number | null;
  scaledScoreMin: number | null;
  scaledScoreMax: number | null;
  classification: string;
}

interface NormalRow {
  id: number;
  standardScoreMin: number | null;
  standardScoreMax: number | null;
  percentileMin: number;
  percentileMax: number;
  classification: string;
}

interface NonNormalRow {
  id: number;
  percentileMin: number;
  percentileMax: number;
  classification: string;
}

const classificationColors: { [key: string]: string } = {
  'Excepcionalmente Alto': '#dc6eee', // Purple
  'Sobre el Promedio': '#2196F3', // Blue
  'Promedio Alto': '#6dc7f0', // Light Blue
  'Promedio': '#4CAF50', // Green
  'Promedio Bajo': '#FFEB3B', // Yellow
  'Bajo el Promedio': '#FF9800', // Orange
  'Excepcionalmente Bajo': '#F44336', // Red
  'Dentro de Expectativas Normales / Dentro de Límites Normales': '#26A69A', // Teal
};

const formatNumber = (num: number | string | null | undefined): string => {
  if (num === null || num === undefined) {
    return '--';
  }

  const numValue = typeof num === 'string' ? parseFloat(num) : num;

  if (isNaN(numValue) || !isFinite(numValue)) {
    return '--';
  }

  if (Number.isInteger(numValue) || Math.abs(numValue % 1) < 0.0001) {
    return Math.round(numValue).toString();
  }
  return numValue.toFixed(1);
};

const formatRange = (min: number | null, max: number | null): string => {
  if (min === null && max === null) return '--';
  if (min === null) return max !== null ? `hasta ${formatNumber(max)}` : '--';
  if (max === null) return `desde ${formatNumber(min)}`;
  if (min === max) return formatNumber(min);
  return `${formatNumber(min)}-${formatNumber(max)}`;
};

const formatPercentile = (min: number, max: number): string => {
  if (Number(min) === Number(max)) return formatNumber(min);
  return `${formatNumber(min)}-${formatNumber(max)}`;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, index, value }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

const OriginalMatrixTable: React.FC<{ matrix: ConversionRow[] }> = ({ matrix }) => (
  <TableContainer component={Paper}>
    <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.4, fontSize: '0.843rem', lineHeight: 1.3 } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Clasificación</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Percentil</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Puntaje Z</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Puntaje T</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Puntaje Estándar</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Puntaje Escalar</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {matrix.map((row) => {
          const bgColor = classificationColors[row.classification] || '#FFFFFF';

          return (
            <TableRow
              key={row.id}
              sx={{
                bgcolor: bgColor,
                '&:hover': {
                  opacity: 0.9
                }
              }}
            >
              <TableCell sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {row.classification}
              </TableCell>
              <TableCell align="center" sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {formatPercentile(row.percentileMin, row.percentileMax)}
              </TableCell>
              <TableCell align="center" sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {formatRange(row.zScoreMin, row.zScoreMax)}
              </TableCell>
              <TableCell align="center" sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {formatRange(row.tScoreMin, row.tScoreMax)}
              </TableCell>
              <TableCell align="center" sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {formatRange(row.standardScoreMin, row.standardScoreMax)}
              </TableCell>
              <TableCell align="center" sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {formatRange(row.scaledScoreMin, row.scaledScoreMax)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const NormalMatrixTable: React.FC<{ matrix: NormalRow[] }> = ({ matrix }) => (
  <TableContainer component={Paper}>
    <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.4, fontSize: '0.843rem', lineHeight: 1.3 } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Clasificación</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Puntaje Estándar</TableCell>
          <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Percentil</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {matrix.map((row) => {
          const bgColor = classificationColors[row.classification] || '#FFFFFF';

          return (
            <TableRow
              key={row.id}
              sx={{
                bgcolor: bgColor,
                '&:hover': {
                  opacity: 0.9
                }
              }}
            >
              <TableCell sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {row.classification}
              </TableCell>
              <TableCell align="center" sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {formatRange(row.standardScoreMin, row.standardScoreMax)}
              </TableCell>
              <TableCell align="center" sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {formatPercentile(row.percentileMin, row.percentileMax)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const NonNormalMatrixTable: React.FC<{ matrix: NonNormalRow[] }> = ({ matrix }) => (
  <TableContainer component={Paper}>
    <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.4, fontSize: '0.843rem', lineHeight: 1.3 } }}>
      <TableHead>
        <TableRow>
          <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Percentil</TableCell>
          <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper', py: 0.6 }}>Clasificación</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {matrix.map((row) => {
          const bgColor = classificationColors[row.classification] || '#FFFFFF';

          return (
            <TableRow
              key={row.id}
              sx={{
                bgcolor: bgColor,
                '&:hover': {
                  opacity: 0.9
                }
              }}
            >
              <TableCell align="center" sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {formatPercentile(row.percentileMin, row.percentileMax)}
              </TableCell>
              <TableCell sx={{ color: '#000000', fontWeight: 'bold', py: 0.4 }}>
                {row.classification}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

const EvaluationMatrixPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [matrix, setMatrix] = useState<ConversionRow[]>([]);
  const [normalMatrix, setNormalMatrix] = useState<NormalRow[]>([]);
  const [nonNormalMatrix, setNonNormalMatrix] = useState<NonNormalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMatrices = async () => {
      try {
        setLoading(true);
        setError(null);
        const [originalRes, normalRes, nonNormalRes] = await Promise.all([
          apiClient.get('/evaluations/matrix'),
          apiClient.get('/evaluations/matrix/normal'),
          apiClient.get('/evaluations/matrix/non-normal'),
        ]);
        setMatrix(originalRes.data.data);
        setNormalMatrix(normalRes.data.data);
        setNonNormalMatrix(nonNormalRes.data.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Error al cargar la matriz de conversión');
      } finally {
        setLoading(false);
      }
    };

    fetchMatrices();
  }, []);

  if (loading) {
    return <LoadingOrb size={64} minHeight="400px" />;
  }

  if (error) {
    return (
      <Box>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/evaluations"
            variant="body1"
            sx={{ cursor: 'pointer' }}
          >
            Evaluaciones
          </Link>
          <Typography color="text.primary">Matriz de Conversión</Typography>
        </Breadcrumbs>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/evaluations"
          variant="body1"
          sx={{ cursor: 'pointer' }}
        >
          Evaluaciones
        </Link>
        <Typography color="text.primary">Matriz de Conversión</Typography>
      </Breadcrumbs>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TableChartIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Matriz de Conversión de Puntajes
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tablas de referencia para clasificar el rendimiento de una evaluación. Al crear una evaluación se podrá elegir cuál de estas 3 clasificaciones usar; por defecto se utiliza la matriz original.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card sx={{ maxWidth: 'fit-content', width: '100%' }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Tabs
              value={tab}
              onChange={(_e, newValue) => setTab(newValue)}
              sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Matriz Original" />
              <Tab label="Distribuciones Normales" />
              <Tab label="Distribuciones No Normales" />
            </Tabs>

            <TabPanel value={tab} index={0}>
              <OriginalMatrixTable matrix={matrix} />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <NormalMatrixTable matrix={normalMatrix} />
            </TabPanel>
            <TabPanel value={tab} index={2}>
              <NonNormalMatrixTable matrix={nonNormalMatrix} />
            </TabPanel>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default EvaluationMatrixPage;
